import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useConnection } from "../../../lib/useConnection";
import type { SettingsServer } from "../../../lib/servers";
import {
  candidateRank,
  classifyCandidate,
  isCustomCandidate,
  type CandidateProbe,
} from "../../../lib/serverConnection";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import { XIcon } from "../../../components/icons";
import {
  getNetworkInfo,
  getPrefs,
  patchPrefs,
  type NetworkInfo,
  type SecureConnections,
  type ServerPrefs,
} from "../../../lib/libraryAdmin";
import {
  fieldOfProblem,
  formFromPrefs,
  interfaceOptions,
  isHttpUrl,
  isValidLanNetwork,
  networkPatch,
  parsePort,
  problemMessage,
  sameForm,
  SECURE_CONNECTIONS_OPTIONS,
  settledForm,
  touchesBroker,
  validateForm,
  WAN_STREAM_OPTIONS,
  type NetworkForm,
} from "../../../lib/networkAdmin";
import { compareVersions } from "../../../lib/generalAdmin";
import { useAdvancedSettings } from "../../../lib/settingsAdvanced";
import { useRemoteAccessCheck } from "../../../lib/useRemoteAccessCheck";
import {
  Checkbox,
  Chip,
  ErrorNote,
  errorMessage,
  Field,
  Select,
  Spinner,
} from "../../../components/ui";
import {
  AdvancedToggle,
  saveOnEnter,
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetNotice,
  SheetSection,
  UnsavedBar,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";
import { RemoteAccessCard } from "../../../components/remote-access";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/network")({
  component: ServerNetwork,
});

/**
 * Plex-style Network page. One draft over the server's prefs, saved from
 * the floating "Unsaved changes" pill (settings-sheet UnsavedBar; Kenny,
 * 2026-10 — until then every control had an Apply of its own): Save sends
 * everything that changed in one PATCH, the public port included, and a
 * saved port has the Remote access card wait for the verdict that follows
 * it. The default view is Remote access (the BROKER's
 * outside-in verdict, the Private → Public → Internet row, the manual public
 * port, the relay and custom URLs), Local network (discovery, LAN networks,
 * WAN-as-LAN) and Webhooks. Show advanced adds this browser's own candidate
 * probes on top, the network-interface advertising control, streaming limits
 * and secure connections. Reachability claims come from Rago's servers; this
 * device's probes stay qualified as diagnostics.
 */

/** The inputs that can carry a line of their own, in page order: the first
 * wrong one takes the focus when Save refuses the page. */
const INPUT_ID: ReadonlyArray<readonly [keyof NetworkForm, string]> = [
  ["manualPublicPort", "net-manual-port"],
  ["customConnections", "net-custom-urls"],
  ["lanNetworks", "net-lan"],
  ["preferredNetworkInterface", "net-iface"],
  ["terminatePausedMinutes", "net-paused"],
  ["webhookUrls", "net-webhook-urls"],
];

/** A control that a revert disables hands the focus to the checkbox that
 * governs it. */
const GOVERNED_BY: Record<string, string> = {
  "net-manual-port": "net-manual-port-mode",
  "net-webhook-urls": "net-webhooks",
};

/** First rago-server release that serves its own web app at /web/: the
 * first public release (0.1.0, 2026-11-04). Unstamped builds (-dev) never
 * compare and get no link. */
const LOCAL_APP_MIN_VERSION = "0.1.0";

interface DeviceRow {
  key: string;
  label: string;
  secondary: string;
  chip: ReactNode;
  /** The server-hosted web app's URL, offered when the secure LAN path failed. */
  localApp?: string;
}

function ProbeChip({ probe, busy }: { probe: CandidateProbe | undefined; busy: boolean }) {
  if (busy) {
    return (
      <span className="text-[var(--rago-text-3)]">
        <Spinner />
      </span>
    );
  }
  if (!probe) return <span className="text-xs text-[var(--rago-text-3)]">—</span>;
  return (
    <span className="shrink-0">
      {probe.ok ? (
        <Chip tone="ok">Reachable</Chip>
      ) : (
        <Chip tone="warn">Not reachable</Chip>
      )}
    </span>
  );
}

/** Bordered list-editor for CIDR/URL prefs (the library wizard's folders
 * shape). The pending input is local state — a background form reseed can
 * never eat a half-typed entry, and it is no part of the page's draft until
 * it is added. Enter adds a row; it never saves the page. */
function StringListEditor(props: {
  id: string;
  values: string[];
  onChange: (values: string[]) => void;
  validate: (v: string) => string | null;
  placeholder: string;
  addLabel: string;
  disabled?: boolean;
}) {
  const [pending, setPending] = useState("");
  const [pendingError, setPendingError] = useState<string | null>(null);

  function add() {
    const v = pending.trim();
    if (!v) return;
    const err = props.validate(v);
    if (err) {
      setPendingError(err);
      return;
    }
    if (!props.values.includes(v)) props.onChange([...props.values, v]);
    setPending("");
    setPendingError(null);
  }

  return (
    <div>
      {props.values.length > 0 ? (
        <ul className="mb-2 divide-y divide-[var(--rago-line-1)] overflow-hidden rounded-[var(--rago-radius-m)] border border-[var(--rago-line-1)]">
          {props.values.map((v) => (
            <li key={v} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="min-w-0 truncate font-mono text-xs text-[var(--rago-text-1)]">
                {v}
              </span>
              <button
                type="button"
                className="shrink-0 text-[var(--rago-text-3)] hover:text-[var(--rago-error)]"
                aria-label={`Remove ${v}`}
                disabled={props.disabled}
                onClick={() => props.onChange(props.values.filter((x) => x !== v))}
              >
                <XIcon size={14} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex items-center gap-2">
        <input
          id={props.id}
          className="rago-input"
          value={pending}
          placeholder={props.placeholder}
          disabled={props.disabled}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          onChange={(e) => {
            setPending(e.target.value);
            setPendingError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button
          type="button"
          className="rago-btn rago-btn-quiet rago-btn-fit shrink-0 text-sm"
          disabled={props.disabled || pending.trim() === ""}
          onClick={add}
        >
          {props.addLabel}
        </button>
      </div>
      {pendingError ? (
        <p role="alert" className="mt-1.5 text-xs text-[var(--rago-error)]">
          {pendingError}
        </p>
      ) : null}
    </div>
  );
}

function ServerNetwork() {
  const { serverId } = Route.useParams();
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // queries would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const conn = isServer ? (source as ServerSource).conn : null;
  const snap = useConnection(serverId);
  const qc = useQueryClient();
  const [advanced] = useAdvancedSettings();

  return (
    <ServerSettingsGuard serverId={serverId} heading="Network">
      {(server) => (
        <NetworkBody
          serverId={serverId}
          server={server}
          conn={conn}
          snap={snap}
          isServer={isServer}
          qc={qc}
          advanced={advanced}
        />
      )}
    </ServerSettingsGuard>
  );
}

function NetworkBody({
  serverId,
  server,
  conn,
  snap,
  isServer,
  qc,
  advanced,
}: {
  serverId: string;
  server: SettingsServer;
  conn: ServerSource["conn"] | null;
  snap: ReturnType<typeof useConnection>;
  isServer: boolean;
  qc: ReturnType<typeof useQueryClient>;
  advanced: boolean;
}) {
  // Arriving fresh (reload) means no live connection yet — kick one so the
  // diagnostics settle on the real route. establish() self-dedupes; the
  // phase guard means this can't loop (phase never returns to "disconnected").
  useEffect(() => {
    if (conn && snap.phase === "disconnected") {
      void conn.establish().catch(() => {});
    }
  }, [conn, snap.phase]);

  // This browser's own candidate probes — diagnostics only (advanced).
  const probes = useQuery({
    queryKey: ["settings", "network-probes", serverId],
    enabled: Boolean(conn) && snap.candidates.length > 0 && advanced,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    queryFn: () => {
      if (!conn) return Promise.resolve([] as CandidateProbe[]);
      return Promise.all(snap.candidates.map((c) => conn.probeCandidate(c)));
    },
  });
  const probeByUri = new Map((probes.data ?? []).map((p) => [p.uri, p]));

  // Owner-only queries: /v1/prefs and /v1/network 403 for guests, so they
  // never fire at all for a non-owner.
  const canAdmin = isServer && server.owned;
  const prefs = useQuery({
    queryKey: ["settings", "prefs", serverId],
    enabled: canAdmin,
    queryFn: () => getPrefs((conn as NonNullable<typeof conn>)),
  });
  // Older servers 404 this route; the enrichment simply stays hidden.
  const net = useQuery({
    queryKey: ["settings", "network-info", serverId],
    enabled: canAdmin,
    retry: false,
    queryFn: () => getNetworkInfo((conn as NonNullable<typeof conn>)),
  });

  const check = useRemoteAccessCheck(serverId);

  // One draft form seeded from the server's values, following them while it
  // is still clean (settings-sheet useDraft).
  const baseline = useMemo(() => (prefs.data ? formFromPrefs(prefs.data) : null), [prefs.data]);
  const [draft, setDraft] = useDraft(baseline, sameForm);

  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof NetworkForm, string>>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  // The control changed last gets the focus back when the pill leaves; one
  // the revert disabled hands it to the checkbox that governs it.
  const touched = useLastTouched((lost) => document.getElementById(GOVERNED_BY[lost.id] ?? ""));
  // The delayed refetch after a broker-facing save: the server kicks a
  // heartbeat on those keys and the candidate list lands a few seconds later.
  const brokerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (brokerTimer.current) clearTimeout(brokerTimer.current);
    },
    [],
  );

  const form = draft ?? baseline;
  // The draft as Save sees it (a port typed and switched off again is no
  // change). Compared field by field, not through the patch: that leaves out
  // a port or minutes value that is no number, and the pill still has to
  // show for it so that Save can say what is wrong.
  const settled = baseline && form ? settledForm(baseline, form) : null;
  const dirty = Boolean(baseline && settled && !sameForm(baseline, settled));

  // Feature detection: a server that predates the network prefs has none of
  // the keys, and phantom toggles would look broken.
  const supported = Boolean(prefs.data && "manualPortMappingMode" in prefs.data);
  const has = (k: keyof ServerPrefs) => Boolean(prefs.data && k in prefs.data);
  const formActive = Boolean(canAdmin && supported && form);

  function update(p: Partial<NetworkForm>) {
    setDraft((f) => ({ ...(f as NetworkForm), ...p }));
    setSaved(false);
    setError(null);
    const keys = Object.keys(p) as (keyof NetworkForm)[];
    setFieldErrors((e) => {
      const next = { ...e };
      for (const k of keys) delete next[k];
      // The port's line belongs to the pair: the mode changing answers it too.
      if ("manualPortMappingMode" in p) delete next.manualPublicPort;
      return next;
    });
  }

  function revert() {
    setDraft(baseline);
    setError(null);
    setFieldErrors({});
  }

  // The lines go under their fields and the first one on the page takes the
  // focus, which brings it into view under the floating pill. A field that
  // is not on the page (hidden with Show advanced) has nowhere to say it:
  // the pill's note.
  function showFieldErrors(errs: Partial<Record<keyof NetworkForm, string>>) {
    setFieldErrors((prev) => ({ ...prev, ...errs }));
    const first = INPUT_ID.find(([field]) => errs[field]);
    if (!first) {
      setError(new Error(Object.values(errs)[0]));
      return;
    }
    const input = document.getElementById(first[1]);
    if (input) input.focus();
    else setError(new Error(errs[first[0]]));
  }

  // One PATCH for everything that changed. The baseline is seeded from the
  // response (PATCH /v1/prefs returns the whole document) and the draft
  // snaps to what the server stored — it normalises (a host bit in a CIDR, a
  // trailing slash, "05" minutes) — unless something was changed while the
  // save was on its way.
  async function save() {
    if (!conn || !form || !baseline || !settled || !prefs.data || saving || !dirty) return;
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      showFieldErrors(errs);
      return;
    }
    const patch = networkPatch(baseline, settled, prefs.data);
    if (Object.keys(patch).length === 0) {
      // Nothing to send (a key this server lacks): the page is clean.
      setDraft(baseline);
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const next = await patchPrefs(conn, patch);
      // A saved port is tested from outside at once: the card waits for that
      // verdict. First, while the cache still holds the one before the save.
      if ("manualPortMappingMode" in patch || "manualPublicPort" in patch) check.awaitVerdict();
      qc.setQueryData(["settings", "prefs", serverId], next);
      setDraft((d) => (d && sameForm(d, form) ? formFromPrefs(next) : d));
      void qc.invalidateQueries({ queryKey: ["settings", "network-info", serverId] });
      if (touchesBroker(patch)) {
        void qc.invalidateQueries({ queryKey: ["resources"] });
        if (brokerTimer.current) clearTimeout(brokerTimer.current);
        brokerTimer.current = setTimeout(() => {
          void qc.invalidateQueries({ queryKey: ["settings", "network-info", serverId] });
          void qc.invalidateQueries({ queryKey: ["resources"] });
        }, 4000);
      }
      setSaved(true);
    } catch (e) {
      const errField = fieldOfProblem(e);
      const message = problemMessage(e);
      if (errField && message) showFieldErrors({ [errField]: message });
      else setError(e);
    } finally {
      setSaving(false);
    }
  }

  // Enter in the port or the minutes field saves the page (the lists' own
  // inputs add a row instead).
  const enterSaves = saveOnEnter(() => void save());

  async function testConnections() {
    if (!conn) return;
    await Promise.allSettled([
      conn.establish(true),
      probes.refetch(),
      qc.invalidateQueries({ queryKey: ["resources"] }),
    ]);
  }

  const info: NetworkInfo | undefined = net.data;

  const remoteAccessCard = (
    <RemoteAccessCard
      server={server}
      info={info}
      snap={snap}
      canAdmin={formActive}
      draft={
        formActive && form
          ? {
              manualPortMappingMode: form.manualPortMappingMode,
              manualPublicPort: form.manualPublicPort,
            }
          : null
      }
      saved={
        baseline
          ? {
              manualPortMappingMode: baseline.manualPortMappingMode,
              manualPublicPort: baseline.manualPublicPort,
            }
          : null
      }
      relayEnabled={baseline ? baseline.relayEnabled : server.relayEnabled}
      onChange={(p) => update(p)}
      portError={fieldErrors.manualPublicPort}
      onPortEnter={enterSaves}
      check={check}
    >
      {formActive && form ? (
        <div className="mt-4">
          {has("relayEnabled") ? (
            <div className="mb-4">
              <Checkbox
                id="net-relay"
                checked={form.relayEnabled}
                onChange={(v) => update({ relayEnabled: v })}
                label="Enable relay"
                hint={
                  info?.relay ? (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {info.relay.enabled ? (
                        <Chip tone={info.relay.connected ? "ok" : "muted"}>
                          {info.relay.connected ? "Connected" : "Enabled, not connected"}
                        </Chip>
                      ) : (
                        <Chip tone="muted">Disabled</Chip>
                      )}
                      <span>Fallback path when a direct connection isn't possible.</span>
                    </span>
                  ) : (
                    "Allow connections through the Rago relay when a direct connection isn't possible."
                  )
                }
              />
            </div>
          ) : null}
          {has("customConnections") ? (
            <Field
              label="Custom server access URLs"
              htmlFor="net-custom-urls"
              hint="Extra ways to reach this server: reverse proxies and VPN addresses."
              error={fieldErrors.customConnections}
            >
              <StringListEditor
                id="net-custom-urls"
                values={form.customConnections}
                onChange={(values) => update({ customConnections: values })}
                validate={(v) => (isHttpUrl(v) ? null : "Enter a full http:// or https:// URL.")}
                placeholder="https://media.example.com:443"
                addLabel="Add URL"
              />
            </Field>
          ) : null}
        </div>
      ) : null}
    </RemoteAccessCard>
  );

  const localNetworkCard =
    formActive && form ? (
      <SheetSection title="Local network">
        {has("localDiscoveryEnabled") ? (
          <div className="mb-4">
            <Checkbox
              id="net-discovery"
              checked={form.localDiscoveryEnabled}
              onChange={(v) => update({ localDiscoveryEnabled: v })}
              label="Enable local network discovery (mDNS)"
              hint="Lets apps on your local network find this server automatically."
            />
          </div>
        ) : null}
        {has("lanNetworks") ? (
          <Field
            label="LAN networks"
            htmlFor="net-lan"
            hint="Addresses treated as local when enforcing streaming restrictions. Empty uses the server's own subnets."
            error={fieldErrors.lanNetworks}
          >
            <StringListEditor
              id="net-lan"
              values={form.lanNetworks}
              onChange={(values) => update({ lanNetworks: values })}
              validate={(v) =>
                isValidLanNetwork(v) ? null : "Enter an IP address or CIDR network (like 192.168.1.0/24)."
              }
              placeholder="192.168.1.0/24"
              addLabel="Add network"
            />
          </Field>
        ) : null}
        {has("treatWanIpAsLan") ? (
          <div className="mb-4">
            <Checkbox
              id="net-wan-as-lan"
              checked={form.treatWanIpAsLan}
              onChange={(v) => update({ treatWanIpAsLan: v })}
              label="Treat WAN IP as LAN"
              hint="Requests from this network's own public IP count as local."
            />
          </div>
        ) : null}
        {advanced && has("preferredNetworkInterface") ? (
          <Field
            label="Preferred network interface"
            htmlFor="net-iface"
            hint={
              info && info.preferredInterfaceFound === false
                ? "The saved interface no longer exists on the server — pick another or Any."
                : "Advertise connections from a single network interface only. Leave on Any unless the server has multiple networks."
            }
            error={fieldErrors.preferredNetworkInterface}
          >
            <Select
              id="net-iface"
              value={form.preferredNetworkInterface}
              onChange={(v) => update({ preferredNetworkInterface: v })}
            >
              {interfaceOptions(info, form.preferredNetworkInterface).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
      </SheetSection>
    ) : null;

  const streamingCard =
    formActive && form && advanced && (has("wanPerUserStreams") || has("terminatePausedMinutes")) ? (
      <SheetSection title="Streaming limits">
        <div className="grid gap-x-4 sm:grid-cols-2 [&>*]:mb-0">
          {has("wanPerUserStreams") ? (
            <Field label="Remote streams allowed per user" htmlFor="net-wan-streams">
              <Select
                id="net-wan-streams"
                value={String(form.wanPerUserStreams)}
                onChange={(v) => update({ wanPerUserStreams: Number(v) })}
              >
                {WAN_STREAM_OPTIONS.map(([value, label]) => (
                  <option key={value} value={String(value)}>
                    {label}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}
          {has("terminatePausedMinutes") ? (
            <Field
              label="Terminate sessions paused longer than"
              htmlFor="net-paused"
              error={fieldErrors.terminatePausedMinutes}
            >
              <div className="relative">
                <input
                  id="net-paused"
                  className="rago-input pr-12"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={form.terminatePausedMinutes}
                  onChange={(e) => update({ terminatePausedMinutes: e.target.value })}
                  onKeyDown={enterSaves}
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[var(--rago-text-3)]"
                >
                  min
                </span>
              </div>
            </Field>
          ) : null}
        </div>
      </SheetSection>
    ) : null;

  const secureCard =
    formActive && form && advanced && has("secureConnections") ? (
      <SheetSection title="Secure connections">
        <Field
          label="Secure connections"
          htmlFor="net-secure"
          hint="Required blocks apps that don't support secure connections."
        >
          <Select
            id="net-secure"
            value={form.secureConnections}
            onChange={(v) => update({ secureConnections: v as SecureConnections })}
          >
            {SECURE_CONNECTIONS_OPTIONS.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
      </SheetSection>
    ) : null;

  const webhooksCard =
    formActive && form && has("webhooksEnabled") ? (
      <SheetSection title="Webhooks">
        <div className="mb-4">
          <Checkbox
            id="net-webhooks"
            checked={form.webhooksEnabled}
            onChange={(v) => update({ webhooksEnabled: v })}
            label="Webhooks"
            hint="Send playback and library events as JSON POSTs to the URLs below."
          />
        </div>
        {has("webhookUrls") ? (
          <Field
            label="Webhook URLs"
            htmlFor="net-webhook-urls"
            error={fieldErrors.webhookUrls}
          >
            {/* Follows the box as ticked, not as saved: the switch and its
                URLs go out in the same Save. */}
            <StringListEditor
              id="net-webhook-urls"
              values={form.webhookUrls}
              onChange={(values) => update({ webhookUrls: values })}
              validate={(v) => (isHttpUrl(v) ? null : "Enter a full http:// or https:// URL.")}
              placeholder="https://hooks.example.com/rago"
              addLabel="Add URL"
              disabled={!form.webhooksEnabled}
            />
          </Field>
        ) : null}
      </SheetSection>
    ) : null;

  // Diagnostics (advanced): what THIS browser can reach — one row per
  // candidate, read Local → Remote → Relay. Never the headline; the relay's
  // own state sits under its checkbox in Remote access.
  const publicAddress = server.publicAddress ?? info?.observedPublicAddress ?? null;
  // The port the broker tests at that address: the manual one, else the
  // server's own.
  const testedPort = baseline?.manualPortMappingMode
    ? parsePort(baseline.manualPublicPort)
    : (info?.port ?? null);
  const deviceRows = (() => {
    // Connection priority order: the owner's custom URLs, then the LAN,
    // the public address, the relay — the same order the client races.
    const sorted = [...snap.candidates].sort((a, b) => candidateRank(a) - candidateRank(b));
    const rows: DeviceRow[] = sorted.map((c): DeviceRow => {
      const kind = classifyCandidate(c);
      const custom = isCustomCandidate(c);
      const probe = probeByUri.get(c.uri);
      // Plex's advice when the secure LAN path can't be used from a browser
      // (a router filtering private DNS answers): open the web app the
      // server serves itself over plain HTTP. Only for servers that ship it.
      const localApp =
        kind === "local" &&
        probe?.ok === false &&
        server.version !== null &&
        (compareVersions(server.version, LOCAL_APP_MIN_VERSION) ?? -1) >= 0
          ? `http://${c.address}:${c.port}/web/`
          : undefined;
      return {
        key: c.uri,
        ...(localApp ? { localApp } : {}),
        label:
          kind === "local"
            ? "Local network"
            : kind === "relay"
              ? "Relay"
              : custom
                ? "Custom URL"
                : "Remote (direct)",
        secondary:
          kind === "relay"
            ? (c.directHostname ?? `${c.address}:${c.port}`)
            : custom
              ? c.uri
              : `${c.address}:${c.port}`,
        chip: <ProbeChip probe={probeByUri.get(c.uri)} busy={probes.isFetching} />,
      };
    });
    // No public candidate published (the broker has not verified the port):
    // still show the public address, with the honest verdict, so the list
    // reads whole.
    if (publicAddress && !sorted.some((c) => !c.local && !c.relay && c.directHostname)) {
      rows.splice(sorted.filter((c) => candidateRank(c) <= 1).length, 0, {
        key: "public-unpublished",
        label: "Remote (direct)",
        secondary: `${publicAddress}:${testedPort ?? "—"}`,
        chip: <Chip tone="warn">Not reachable</Chip>,
      });
    }
    return rows;
  })();
  const deviceCard = advanced ? (
    <SheetSection
      title="Connections from this device"
      action={
        <button
          type="button"
          className="rago-btn rago-btn-quiet rago-btn-sm"
          onClick={() => void testConnections()}
          disabled={snap.phase === "connecting" || probes.isFetching}
        >
          Test connections
        </button>
      }
    >
      {deviceRows.length === 0 ? (
        <p className="text-sm text-[var(--rago-text-3)]">
          No secure endpoints yet — connection candidates appear once the server has finished
          setting up its HTTPS certificate.
        </p>
      ) : (
        <ul className="space-y-2">
          {deviceRows.map((r) => (
            <li
              key={r.key}
              className="flex items-center justify-between gap-4 rounded-[var(--rago-radius-m)] border border-[var(--rago-line-1)] bg-[rgba(0,0,0,0.28)] px-4 py-3"
            >
              <div className="min-w-0">
                <p className="text-sm text-[var(--rago-text-1)]">{r.label}</p>
                <p className="mt-0.5 truncate font-mono text-xs text-[var(--rago-text-3)]">
                  {r.secondary}
                </p>
                {r.localApp ? (
                  <a
                    href={r.localApp}
                    target="_blank"
                    rel="noreferrer"
                    className="rago-link mt-1 inline-block text-xs underline underline-offset-2"
                  >
                    Open the local app
                  </a>
                ) : null}
              </div>
              {r.chip}
            </li>
          ))}
        </ul>
      )}
    </SheetSection>
  ) : null;

  const loadingCard =
    canAdmin && (prefs.isLoading || (!form && !prefs.isError)) ? (
      <SheetLoading />
    ) : canAdmin && prefs.data && !supported ? (
      <SheetNotice title="Network settings">
        This server doesn't support network settings yet — update rago-server.
      </SheetNotice>
    ) : null;

  return (
    // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
    <div className="rago-sheet-fill">
      <SheetHeader title="Network" action={<AdvancedToggle />} />
      {deviceCard}
      {/* Every control's change bubbles to here: the one changed last. */}
      <div onChange={touched.onChange}>
        {remoteAccessCard}
        {loadingCard}
        {localNetworkCard}
        {streamingCard}
        {secureCard}
        {webhooksCard}
      </div>
      {canAdmin && prefs.error ? (
        <SheetInset>
          <ErrorNote error={prefs.error} />
        </SheetInset>
      ) : null}

      {/* The root's direct child (its dock rides at the sheet's foot), and
          always rendered, as UnsavedBar asks. Someone the server is shared
          with has no form, and so never anything unsaved. */}
      <UnsavedBar
        dirty={dirty}
        saving={saving}
        saved={saved}
        error={error ? errorMessage(error) : null}
        onSave={() => void save()}
        onRevert={revert}
        onFocusLost={touched.focus}
      />
    </div>
  );
}
